import type {
  ComponentProps,
  ComponentType,
  CSSProperties,
  ElementType,
} from "react";
import { Box, TextField } from "@mui/material";
import type {
  AutocompleteProps,
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import type { PaperProps } from "@mui/material/Paper";
import cx from "classnames";
import { SearchIcon } from "@ui-icons";

// Type for the inline popper component props
interface PopperProps {
  style?: CSSProperties;
  className?: string;
  [key: string]: any;
}

// Inline popper component that forces static positioning
const InlinePopper = (props: PopperProps) => {
  // destructuring "anchorEl" and "disablePortal" to avoid errors as they are invalid attributes
  const { style, anchorEl, disablePortal, ...other } = props;
  return (
    <div
      {...other}
      style={{
        ...style,
        position: "static",
        transform: "none !important",
        width: "100%",
      }}
    />
  );
};

// Inline paper: renders the dropdown surface inline (not as a floating popup).
// Strips `elevation` (a Paper-only prop that would otherwise reach the DOM via Box) and
// forces `border: none` so the global Autocomplete paper border, intended for floating
// dropdowns, is not applied to the inline variant.
const InlinePaper = ({ elevation, sx, ...paperProps }: PaperProps) => (
  <Box
    {...paperProps}
    sx={[{ border: "none !important" }, ...(Array.isArray(sx) ? sx : [sx])].filter(
      Boolean
    )}
  />
);

// HOC that adds the inline popper to any component with slots prop
export function withInlinePopper<P extends { slots?: Record<string, ElementType> }>(
  Component: ComponentType<P>
) {
  function WithInlinePopper(props: P) {
    // Merge the provided slots with our custom popper
    const slots = {
      ...(props.slots || {}),
      popper: InlinePopper,
    };

    // Return the wrapped component with the inline popper
    return <Component {...props} slots={slots} />;
  }

  // Set display name for debugging
  const displayName = Component.displayName || Component.name || "Component";
  WithInlinePopper.displayName = `withInlinePopper(${displayName})`;

  return WithInlinePopper;
}

// Type-specific version for Autocomplete-based components
export function withInlineAutocomplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  Component: ComponentType<
    ComponentProps<any> & {
      slots?: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>["slots"];
      slotProps?: any;
      open?: boolean;
    }
  >,
  options?: {
    renderInputClassName?: string;
  }
) {
  function WithInlineAutocomplete(props: ComponentProps<typeof Component>) {
    // Combine the inline popper with forcing the dropdown to be open by default
    const slots = {
      ...(props.slots || {}),
      popper: InlinePopper,
      // Forward full Autocomplete Paper slot props (incl. slotProps.paper: data-test-id, sx).
      // Previous plain div dropped everything except className/style.
      paper: InlinePaper,
    };

    // Pass all props and override specific ones
    return (
      <Component
        {...props}
        sx={{
          width: "100%",
          "&[data-options-count='1']": { display: "none" },
          ...props.sx,
        }}
        open={props.open ?? true}
        slots={slots}
        data-options-count={props.options?.length ?? 0}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <div className={cx("p-3", options?.renderInputClassName)}>
            <TextField
              {...params}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  sx: props.slotProps?.renderInput?.TextField?.slotProps?.input?.sx,
                  startAdornment: (
                    <>
                      <SearchIcon color="action" />
                      {params.slotProps.input?.startAdornment}
                    </>
                  ),
                },
              }}
              autoFocus
              label={props.placeholder}
            />
          </div>
        )}
      />
    );
  }

  // Set display name for debugging
  const displayName = Component.displayName || Component.name || "Component";
  WithInlineAutocomplete.displayName = `withInlineAutocomplete(${displayName})`;

  return WithInlineAutocomplete;
}
